/**
 * UserMenu — avatar + nombre + role en el topbar, con dropdown que
 * permite cerrar sesión.
 *
 * Sustituye el botón "Cerrar sesión" inline que vivía en HomeView. La
 * lógica de logout vía MSAL es idéntica.
 *
 * En B.5.c.1 el dropdown solo trae "sign out". En versiones futuras
 * agregaremos: "Mi perfil", "Preferencias", "Atajos de teclado".
 */

import { useEffect, useRef, useState } from 'react'
import { useAccount, useMsal } from '@azure/msal-react'
import { Link } from '@tanstack/react-router'
import { Bell, Bot, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { MeResponse } from '../../api/me'
import { useSignOut } from '../../auth/useSignOut'
import { canUseAgentAccess, isKnownTier, tierLabelKey } from '../../lib/tiers'

interface UserMenuProps {
  me: MeResponse | undefined
}

export function UserMenu({ me }: UserMenuProps) {
  const { t } = useTranslation(['shell', 'team'])
  const { accounts } = useMsal()
  const account = useAccount(accounts[0]) ?? null
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Cierra el dropdown al click afuera o Escape.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const displayName = me?.display_name ?? account?.name ?? '—'
  const role = me?.role ?? null
  const roleLabel = role
    ? isKnownTier(role)
      ? t(`team:${tierLabelKey(role)}`)
      : role
    : null
  const email = me?.email ?? account?.username ?? null
  const initials = buildInitials(displayName)

  const signOut = useSignOut()

  const handleLogout = () => {
    setOpen(false)
    signOut()
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-muted transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-brand text-canvas grid place-items-center text-xs font-semibold">
          {initials}
        </div>
        <div className="text-left hidden md:block">
          <p className="text-xs font-medium leading-none text-ink">
            {displayName}
          </p>
          {roleLabel && (
            <p className="text-[10px] text-ink-subtle leading-none mt-0.5">
              {roleLabel}
            </p>
          )}
        </div>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-64 rounded-md border border-line bg-surface shadow-lg z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-line">
            <p className="text-sm font-medium text-ink truncate">{displayName}</p>
            {email && (
              <p className="text-xs text-ink-subtle font-mono truncate mt-0.5">
                {email}
              </p>
            )}
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
              {roleLabel && (
                <>
                  <dt className="text-ink-subtle">{t('user_menu.role_label')}</dt>
                  <dd className="text-ink truncate">{roleLabel}</dd>
                </>
              )}
              {!me?.tenant && (
                <>
                  <dt className="text-ink-subtle">{t('user_menu.organization_label')}</dt>
                  <dd className="text-review">{t('user_menu.limbo')}</dd>
                </>
              )}
            </dl>
          </div>
          <Link
            to="/settings/notifications"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors text-left border-b border-line"
          >
            <Bell className="w-4 h-4" aria-hidden="true" />
            {t('user_menu.notification_prefs')}
          </Link>
          {canUseAgentAccess(role) && (
            <Link
              to="/settings/agents"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors text-left border-b border-line"
            >
              <Bot className="w-4 h-4" aria-hidden="true" />
              {t('user_menu.connected_agents')}
            </Link>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors text-left"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            {t('user_menu.sign_out')}
          </button>
        </div>
      )}
    </div>
  )
}

function buildInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '··'
  if (parts.length === 1) {
    const word = parts[0] ?? ''
    return word.slice(0, 2).toUpperCase()
  }
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}
